import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getNetworkConfig, requestFaucet, getWalletBalances } from "@/lib/api";
import { useWallet, isSocialLoginEnabled } from "@/contexts/WalletContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Loader2, XCircle, Rocket, Wallet, Globe, Droplets, Coins, BookOpen, Hammer } from "lucide-react";
import { motion } from "framer-motion";
import { StarterTokenDeploy } from "@/components/quickstart/StarterTokenDeploy";
import { FADE_UP } from "@/lib/motion";
import { TestTokenNote } from "@/components/common/TestTokenNote";

const STEPS = [
  { id: "connect", title: "Connect your wallet", desc: "MetaMask, or WalletConnect if it's configured", icon: Wallet },
  { id: "network", title: "Add FMI Smart Chain", desc: "Adds chain 77177 to your wallet and switches to it", icon: Globe },
  { id: "fmi", title: "Claim 10 FMI", desc: "Pays for gas on FSC", icon: Droplets },
  { id: "usdt", title: "Claim 100 USDT", desc: "Also adds the USDT token to your wallet", icon: Coins },
  { id: "deploy", title: "Deploy a starter token", desc: "Optional: a one-click ERC-20 that you own (below)", icon: Hammer },
  { id: "docs", title: "Open the developer docs", desc: "ethers.js, Hardhat and Foundry guides", icon: BookOpen },
];

const StatusIcon = ({ state }) => {
  if (state === "done") return <CheckCircle2 size={20} className="text-green-400" />;
  if (state === "running") return <Loader2 size={20} className="text-[#D4AF37] animate-spin" />;
  if (state === "error") return <XCircle size={20} className="text-red-400" />;
  if (state === "ready") return <Circle size={20} className="text-[#D4AF37]" />;
  if (state === "skipped") return <CheckCircle2 size={20} className="text-zinc-500" />;
  return <Circle size={20} className="text-zinc-700" />;
};

// Faucet requests return once broadcast; wait until gas money has actually landed (~3s blocks)
const waitForFunds = async (addr) => {
  for (let i = 0; i < 15; i++) {
    const bal = await getWalletBalances(addr).catch(() => null);
    if (bal?.fmi > 0) return;
    await new Promise((r) => setTimeout(r, 1000));
  }
};

export default function QuickstartPage() {
  const navigate = useNavigate();
  const { address, connect, connectSocial, addFSCNetwork, addUSDTToken, refreshBalances } = useWallet();
  const { data: config } = useQuery({ queryKey: ["networkConfig"], queryFn: getNetworkConfig });
  const [states, setStates] = useState({});
  const [notes, setNotes] = useState({});
  const [running, setRunning] = useState(false);

  const set = (id, state, note) => {
    setStates((s) => ({ ...s, [id]: state }));
    if (note !== undefined) setNotes((n) => ({ ...n, [id]: note }));
  };

  const claim = async (addr, token) => {
    try {
      const res = await requestFaucet(addr, token);
      return { ok: true, note: `${res.amount} sent · ${res.txHash.slice(0, 12)}…` };
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Request failed";
      // Recently claimed counts as done for onboarding
      return status === 429 && detail.startsWith("Rate limited") ? { ok: true, skipped: true, note: `Already claimed recently. ${detail}` } : { ok: false, note: detail };
    }
  };

  const runAll = async () => {
    setRunning(true);
    setStates({});
    setNotes({});
    try {
      set("connect", "running");
      const addr = address || (window.ethereum ? await connect() : isSocialLoginEnabled ? await connectSocial() : null);
      if (!addr) {
        set("connect", "error", window.ethereum || isSocialLoginEnabled ? "Wallet connection was not completed" : "No wallet found. Install MetaMask to continue.");
        return;
      }
      set("connect", "done", addr);

      set("network", "running");
      if (!(await addFSCNetwork())) {
        set("network", "error", "The network was not added. Approve the request in your wallet and try again.");
        return;
      }
      set("network", "done", "FSC selected in your wallet");

      for (const [id, token] of [["fmi", "FMI"], ["usdt", "USDT"]]) {
        set(id, "running");
        const r = await claim(addr, token);
        set(id, r.ok ? (r.skipped ? "skipped" : "done") : "error", r.note);
        if (!r.ok) return;
      }
      if (config?.usdt?.address) await addUSDTToken(config.usdt.address, config.usdt.decimals);
      set("deploy", "running", "Waiting for your FMI to confirm…");
      await waitForFunds(addr);
      setTimeout(() => refreshBalances(addr), 4000);

      set("deploy", "ready", "Ready. Deploy your token below.");
    } finally {
      setRunning(false);
    }
  };

  const allDone = Boolean(states.deploy);
  const openDocs = () => {
    set("docs", "done");
    navigate("/docs");
  };

  return (
    <div data-testid="quickstart-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP}>
        <div className="mb-8">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            Get <span className="text-[#D4AF37]">started</span>
          </h1>
          <p className="text-zinc-400">One click sets up your wallet for FMI Smart Chain and funds it with free FMI and USDT.</p>
          <TestTokenNote testId="quickstart-test-token-note" className="mt-3" />
        </div>

        <Card className="border-[#2A2411] bg-[#0A0A0A] gold-glow">
          <CardContent className="p-6 space-y-6">
            <ol className="space-y-4">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                const state = states[step.id];
                return (
                  <li key={step.id} data-testid={`quickstart-step-${step.id}`} data-state={state || "idle"}
                    className={`flex items-start gap-4 p-3 rounded-lg border transition-colors ${state === "running" ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-[#2A2411] bg-[#050505]"}`}>
                    <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{i + 1}. {step.title}</p>
                      <p className="text-xs text-zinc-500">{step.desc}</p>
                      {notes[step.id] && (
                        <p data-testid={`quickstart-note-${step.id}`} className={`text-xs mt-1 font-mono break-all ${state === "error" ? "text-red-400" : "text-zinc-400"}`}>{notes[step.id]}</p>
                      )}
                    </div>
                    <StatusIcon state={state} />
                  </li>
                );
              })}
            </ol>

            <Button data-testid="quickstart-run-btn" onClick={runAll} disabled={running}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-12 rounded-md transition-transform active:scale-[0.98]">
              {running ? <><Loader2 size={16} className="animate-spin mr-2" /> Setting up…</> : allDone ? "Run again" : <><Rocket size={16} className="mr-2" /> Get started in one click</>}
            </Button>
            <p className="text-[11px] text-zinc-600 text-center">Your wallet will ask you to approve the connection, the network, and the USDT token.</p>
          </CardContent>
        </Card>

        {(allDone || address) && (
          <Card className="border-[#2A2411] bg-[#0A0A0A] mt-6">
            <CardContent className="p-6 space-y-6">
              <StarterTokenDeploy onDeployed={(t) => set("deploy", "done", `${t.symbol} at ${t.address}`)} />
              <Button data-testid="quickstart-open-docs-btn" variant="outline" onClick={openDocs}
                className="w-full border-[#2A2411] text-zinc-300 hover:text-white hover:bg-[#D4AF37]/10">
                <BookOpen size={16} className="mr-2" /> Open the developer docs
              </Button>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
